import { ArrowLeftIcon, LifeBuoyIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateSupportRequest } from '@/features/org/queries';
import { errorMessage } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import type { SupportPriority, SupportStatus } from '@/lib/types';
import { cn } from '@/lib/utils';
import { SupportThread } from './SupportThread';
import { useReplyToRequest, useSupportMessages, useSupportRequest } from './queries';

export const STATUS_LABEL: Record<SupportStatus, string> = { new: 'New', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' };
export const PRIORITY_LABEL: Record<SupportPriority, string> = { low: 'Low', normal: 'Normal', high: 'High' };

/**
 * One help request on its own page: the conversation with the requester (replies, internal notes,
 * screenshots) beside the request's details and its status and priority controls. Organization
 * admins get the full view at /org/support/:id; the person who filed the request gets the
 * requester view at /support/:id, without internal notes or controls.
 */
export function SupportRequestPage({ mode }: { mode: 'admin' | 'requester' }) {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const req = useSupportRequest(requestId);
  const messages = useSupportMessages(requestId);
  const reply = useReplyToRequest(requestId);
  const update = useUpdateSupportRequest();
  const r = req.data?.request;
  const [notes, setNotes] = useState('');
  useEffect(() => setNotes(r?.internalNotes ?? ''), [r?.id, r?.internalNotes]);
  const backHref = mode === 'admin' ? '/org/support' : '/support';

  if (req.isError) return <ErrorState error={req.error} onRetry={() => void req.refetch()} title="Could not load this request" />;
  if (!r) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const save = (body: { status?: SupportStatus; priority?: SupportPriority; internalNotes?: string | null }) =>
    update.mutateAsync({ id: r.id, body }).then(
      () => {
        toast.success('Request updated');
        void req.refetch();
      },
      (err: unknown) => toast.error(errorMessage(err, 'Update failed')),
    );

  return (
    <div className="space-y-8">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="min-w-0">{r.subject}</span>
            <Badge variant={r.status === 'new' ? 'default' : 'secondary'}>{STATUS_LABEL[r.status]}</Badge>
            {r.priority === 'high' && <Badge variant="destructive">High priority</Badge>}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-mono text-xs">{r.reference}</span>
            <span>{r.categoryLabel}</span>
            <span>Received {fmtDateTime(r.createdAt)}</span>
            {r.resolvedAt && <span>Resolved {fmtDateTime(r.resolvedAt)}</span>}
          </span>
        }
        actions={
          <Button asChild variant="outline">
            <Link to={backHref}>
              <ArrowLeftIcon /> {mode === 'admin' ? 'All requests' : 'My requests'}
            </Link>
          </Button>
        }
      />

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <SupportThread request={r} messages={messages.data?.items} loading={messages.isPending} viewer={mode === 'admin' ? 'staff' : 'requester'} onReply={(input) => reply.mutateAsync(input)} busy={reply.isPending} />

        <aside className="grid gap-6 lg:sticky lg:top-24">
          <section className="bg-card grid gap-3 border p-4 text-sm">
            <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Requester</h2>
            <div className="font-medium">{r.requester.name}</div>
            <a className="flex items-center gap-2 underline-offset-4 hover:underline" href={`mailto:${r.requester.email}?subject=${encodeURIComponent(`[${r.reference}] ${r.subject}`)}`}>
              <MailIcon className="text-muted-foreground size-3.5" aria-hidden /> {r.requester.email}
            </a>
            <div className="flex items-center gap-2">
              <PhoneIcon className="text-muted-foreground size-3.5" aria-hidden /> {r.requester.phone ?? <span className="text-muted-foreground">No phone given</span>}
            </div>
            <div className="text-muted-foreground text-xs">{r.requester.userId ? 'Filed while signed in' : 'Filed from the sign-in page'}</div>
            {r.pageUrl && (
              <div className="text-muted-foreground text-xs break-all">
                From <span className="text-foreground">{r.pageUrl}</span>
              </div>
            )}
          </section>

          {mode === 'admin' ? (
            <>
              <section className="bg-card grid gap-4 border p-4">
                <div className="grid gap-2">
                  <Label htmlFor="req-status">Status</Label>
                  <Select value={r.status} onValueChange={(v) => void save({ status: v as SupportStatus })} disabled={update.isPending}>
                    <SelectTrigger id="req-status" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(STATUS_LABEL) as SupportStatus[]).map((s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="req-priority">Priority</Label>
                  <Select value={r.priority} onValueChange={(v) => void save({ priority: v as SupportPriority })} disabled={update.isPending}>
                    <SelectTrigger id="req-priority" className={cn('w-full', r.priority === 'high' && 'text-destructive')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PRIORITY_LABEL) as SupportPriority[]).map((p) => (
                        <SelectItem key={p} value={p}>
                          {PRIORITY_LABEL[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </section>
              <section className="bg-card grid gap-2 border p-4">
                <Label htmlFor="req-notes">Internal notes</Label>
                <Textarea id="req-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Summary for your team. Never shown to the requester." />
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" disabled={update.isPending || notes === (r.internalNotes ?? '')} onClick={() => void save({ internalNotes: notes.trim() ? notes : null })}>
                    Save notes
                  </Button>
                </div>
              </section>
            </>
          ) : (
            <section className="text-muted-foreground flex items-start gap-3 border p-4 text-sm">
              <LifeBuoyIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
              <p>Replies from the team show up here and in your email. Add anything that helps, like a screenshot of what you saw.</p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
